export interface JsonToken {
  text: string;
  /** Undefined for the punctuation/whitespace between highlighted tokens. */
  color?: string;
}

// Contrast-checked against the rendered background (MUI `grey.100`, #f5f5f5).
// All four clear the WCAG AA 4.5:1 minimum for body text.
export const TOKEN_COLORS = {
  key: '#0b5fff', // 4.70:1
  string: '#0a7a37', // 5.00:1
  number: '#b5540a', // 4.55:1
  literal: '#9b2fae', // 5.65:1
};

/*
 * The pattern is assembled from three named parts rather than written as one
 * literal: as a single expression it is dense enough to trip Sonar's regex
 * complexity limit, and far harder to read than the sum of its pieces.
 *
 * A string is `"`, any run of escapes or non-quote characters, `"`, plus the
 * optional trailing colon that makes it an object key. The whitespace before
 * that colon is only consumed when a colon actually follows, so a string
 * *value* does not absorb the newline after it.
 *
 * `\\.` covers every escape including `\uXXXX` — the four hex digits after it
 * are ordinary characters, so they need no separate alternative.
 */
const STRING_TOKEN = String.raw`"(?:\\.|[^"\\])*"(?:\s*:)?`;
const LITERAL_TOKEN = String.raw`\b(?:true|false|null)\b`;
const NUMBER_TOKEN = String.raw`-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?`;

const JSON_TOKEN_SOURCE = [STRING_TOKEN, LITERAL_TOKEN, NUMBER_TOKEN].join('|');

const classifyToken = (token: string): string => {
  if (token.startsWith('"')) {
    return token.trimEnd().endsWith(':')
      ? TOKEN_COLORS.key
      : TOKEN_COLORS.string;
  }

  if (token === 'true' || token === 'false' || token === 'null') {
    return TOKEN_COLORS.literal;
  }

  return TOKEN_COLORS.number;
};

/**
 * Splits pretty-printed JSON into coloured and uncoloured runs for display.
 *
 * Deliberately a lexer rather than a parser: JSONB payloads from PDGS have no
 * fixed shape, and this only needs enough structure to colour keys, strings,
 * numbers and literals — not to validate or interpret them. Concatenating
 * every token's `text` always reproduces the input exactly.
 *
 * The `RegExp` is built per call rather than shared at module scope: a `g`
 * regex carries `lastIndex` between uses, which makes a shared instance
 * order-dependent across callers.
 */
export const tokenizeJson = (json: string): JsonToken[] => {
  const pattern = new RegExp(JSON_TOKEN_SOURCE, 'g');
  const tokens: JsonToken[] = [];
  let lastIndex = 0;

  let match = pattern.exec(json);

  while (match !== null) {
    if (match.index > lastIndex) {
      tokens.push({ text: json.slice(lastIndex, match.index) });
    }

    tokens.push({ text: match[0], color: classifyToken(match[0]) });
    lastIndex = match.index + match[0].length;
    match = pattern.exec(json);
  }

  if (lastIndex < json.length) {
    tokens.push({ text: json.slice(lastIndex) });
  }

  return tokens;
};
